<template>
  <div class="w-full max-w-md mx-auto">
    <div class="text-center mb-8">
      <h2 class="text-3xl font-bold text-gray-900">비밀번호 찾기</h2>
      <p class="mt-2 text-gray-600">아이디 찾기 또는 비밀번호 재설정을 할 수 있습니다</p>
    </div>

    <!-- 탭 선택 -->
    <div class="flex border-b border-gray-200 mb-8">
      <button
        @click="activeTab = 'findId'"
        class="flex-1 py-2 px-4 text-center text-sm font-medium border-b-2 transition-colors"
        :class="{
          'border-primary-500 text-primary-600': activeTab === 'findId',
          'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300': activeTab !== 'findId'
        }"
      >
        아이디 찾기
      </button>
      <button
        @click="activeTab = 'resetPassword'"
        class="flex-1 py-2 px-4 text-center text-sm font-medium border-b-2 transition-colors"
        :class="{
          'border-primary-500 text-primary-600': activeTab === 'resetPassword',
          'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300': activeTab !== 'resetPassword'
        }"
      >
        비밀번호 재설정
      </button>
    </div>

    <!-- 아이디 찾기 -->
    <div v-if="activeTab === 'findId'" class="space-y-6">
      <div class="text-center mb-6">
        <UserIcon class="w-12 h-12 text-primary-600 mx-auto mb-3" />
        <h3 class="text-lg font-semibold text-gray-900">아이디 찾기</h3>
        <p class="text-sm text-gray-600 mt-2">가입 시 입력한 이름과 전화번호로 아이디를 찾을 수 있습니다</p>
      </div>

      <form @submit.prevent="handleFindId" class="space-y-4">
        <!-- 이름 -->
        <div>
          <label for="findId-name" class="form-label">이름 *</label>
          <input
            id="findId-name"
            v-model="findIdForm.name"
            type="text"
            required
            class="form-input"
            :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.findId.name }"
            placeholder="홍길동"
          />
          <p v-if="errors.findId.name" class="form-error">{{ errors.findId.name }}</p>
        </div>

        <!-- 전화번호 -->
        <div>
          <label for="findId-phone" class="form-label">전화번호 *</label>
          <input
            id="findId-phone"
            v-model="findIdForm.phone"
            type="tel"
            required
            class="form-input"
            :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.findId.phone }"
            placeholder="010-1234-5678"
          />
          <p v-if="errors.findId.phone" class="form-error">{{ errors.findId.phone }}</p>
        </div>

        <button
          type="submit"
          :disabled="isLoading"
          class="btn btn-primary btn-lg w-full"
          :class="{ 'opacity-50 cursor-not-allowed': isLoading }"
        >
          <span v-if="isLoading" class="flex items-center justify-center">
            <div class="spinner w-5 h-5 mr-2"></div>
            검색 중...
          </span>
          <span v-else>아이디 찾기</span>
        </button>

        <!-- 결과 표시 -->
        <div v-if="foundId" class="bg-green-50 border border-green-200 rounded-md p-4">
          <div class="flex items-center">
            <CheckCircleIcon class="w-5 h-5 text-green-400 mr-2" />
            <p class="text-green-800 font-medium">아이디를 찾았습니다!</p>
          </div>
          <p class="text-green-700 mt-2">
            등록된 아이디: <strong>{{ foundId }}</strong>
          </p>
          <RouterLink to="/login" class="btn btn-primary btn-sm mt-3">
            로그인하기
          </RouterLink>
        </div>

        <div v-if="errors.findId.general" class="bg-red-50 border border-red-200 rounded-md p-3">
          <p class="text-red-600 text-sm">{{ errors.findId.general }}</p>
        </div>
      </form>
    </div>

    <!-- 비밀번호 재설정 -->
    <div v-if="activeTab === 'resetPassword'" class="space-y-6">
      <div class="text-center mb-6">
        <KeyIcon class="w-12 h-12 text-primary-600 mx-auto mb-3" />
        <h3 class="text-lg font-semibold text-gray-900">비밀번호 재설정</h3>
        <p class="text-sm text-gray-600 mt-2">등록된 이메일로 임시 비밀번호를 발송해드립니다</p>
      </div>

      <form @submit.prevent="handleResetPassword" class="space-y-4">
        <!-- 이메일 -->
        <div>
          <label for="reset-email" class="form-label">이메일 *</label>
          <input
            id="reset-email"
            v-model="resetForm.email"
            type="email"
            required
            class="form-input"
            :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.reset.email }"
            placeholder="example@email.com"
          />
          <p v-if="errors.reset.email" class="form-error">{{ errors.reset.email }}</p>
        </div>

        <button
          type="submit"
          :disabled="isLoading"
          class="btn btn-primary btn-lg w-full"
          :class="{ 'opacity-50 cursor-not-allowed': isLoading }"
        >
          <span v-if="isLoading" class="flex items-center justify-center">
            <div class="spinner w-5 h-5 mr-2"></div>
            발송 중...
          </span>
          <span v-else>임시 비밀번호 발송</span>
        </button>

        <!-- 성공 메시지 -->
        <div v-if="resetSuccess" class="bg-green-50 border border-green-200 rounded-md p-4">
          <div class="flex items-center">
            <CheckCircleIcon class="w-5 h-5 text-green-400 mr-2" />
            <p class="text-green-800 font-medium">임시 비밀번호가 발송되었습니다!</p>
          </div>
          <p class="text-green-700 mt-2 text-sm">
            이메일을 확인하신 후 임시 비밀번호로 로그인해주세요.<br>
            로그인 후 반드시 비밀번호를 변경해주시기 바랍니다.
          </p>
          <RouterLink to="/login" class="btn btn-primary btn-sm mt-3">
            로그인하기
          </RouterLink>
        </div>

        <div v-if="errors.reset.general" class="bg-red-50 border border-red-200 rounded-md p-3">
          <p class="text-red-600 text-sm">{{ errors.reset.general }}</p>
        </div>
      </form>
    </div>

    <!-- 로그인 링크 -->
    <div class="mt-8 text-center border-t pt-6">
      <p class="text-gray-600">
        계정이 기억나셨나요?
        <RouterLink
          to="/login"
          class="text-primary-600 hover:text-primary-500 font-medium"
        >
          로그인하기
        </RouterLink>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { RouterLink } from 'vue-router'
import {
  UserIcon,
  KeyIcon,
  CheckCircleIcon
} from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'

const authStore = useAuthStore()
const toast = useToast()

const activeTab = ref<'findId' | 'resetPassword'>('findId')
const isLoading = ref(false)
const foundId = ref('')
const resetSuccess = ref(false)

// 폼 데이터
const findIdForm = reactive({
  name: '',
  phone: ''
})

const resetForm = reactive({
  email: ''
})

// 에러 상태
const errors = reactive({
  findId: {
    name: '',
    phone: '',
    general: ''
  },
  reset: {
    email: '',
    general: ''
  }
})

// 에러 초기화
const clearErrors = (type: 'findId' | 'reset') => {
  if (type === 'findId') {
    errors.findId.name = ''
    errors.findId.phone = ''
    errors.findId.general = ''
  } else {
    errors.reset.email = ''
    errors.reset.general = ''
  }
}

// 아이디 찾기
const handleFindId = async () => {
  clearErrors('findId')
  
  if (!findIdForm.name.trim()) {
    errors.findId.name = '이름을 입력해주세요.'
    return
  }
  
  if (!findIdForm.phone.trim()) {
    errors.findId.phone = '전화번호를 입력해주세요.'
    return
  }
  
  if (!/^01(?:0|1|[6-9])-(?:\d{3}|\d{4})-\d{4}$/.test(findIdForm.phone)) {
    errors.findId.phone = '올바른 전화번호 형식을 입력해주세요.'
    return
  }

  try {
    isLoading.value = true
    const result = await authStore.findId(findIdForm.name, findIdForm.phone)
    if (result) {
      foundId.value = result
      toast.success('아이디를 찾았습니다!')
    }
  } catch (error: any) {
    errors.findId.general = '일치하는 사용자 정보를 찾을 수 없습니다.'
    toast.error('일치하는 사용자 정보를 찾을 수 없습니다.')
  } finally {
    isLoading.value = false
  }
}

// 비밀번호 재설정
const handleResetPassword = async () => {
  clearErrors('reset')
  
  if (!resetForm.email.trim()) {
    errors.reset.email = '이메일을 입력해주세요.'
    return
  }
  
  if (!/\S+@\S+\.\S+/.test(resetForm.email)) {
    errors.reset.email = '올바른 이메일 형식을 입력해주세요.'
    return
  }

  try {
    isLoading.value = true
    const success = await authStore.resetPassword(resetForm.email)
    if (success) {
      resetSuccess.value = true
      toast.success('임시 비밀번호가 이메일로 발송되었습니다!')
    }
  } catch (error: any) {
    errors.reset.general = '등록되지 않은 이메일이거나 오류가 발생했습니다.'
    toast.error('등록되지 않은 이메일이거나 오류가 발생했습니다.')
  } finally {
    isLoading.value = false
  }
}
</script>